import {
  type Classroom,
  gradeOverview,
  type HomeworkAssignment,
  type HomeworkRecord,
  type HomeworkStudentRecord,
  type Student,
  studentNumberLabel,
} from "@classroom/domain";
import { i18n } from "../i18n";
import { assignmentRecordLabels } from "./assignmentRecordLabels";
import type { RecordReport } from "./recordExports";

export function courseworkCellText(assignment: HomeworkAssignment, record?: HomeworkStudentRecord) {
  const t = i18n.getFixedT(null, "gradeOverview");
  if (!record) return t("table.unrecorded");
  const status = assignmentRecordLabels(assignment.category).statuses[record.status];
  const score = record.score !== undefined && Number.isFinite(record.score) ? String(record.score) : undefined;
  if (record.status === "excused") return status;
  if (score !== undefined) return record.status === "complete" ? score : `${score}\n${status}`;
  return record.status === "complete"
    ? t("table.unscored")
    : record.status === "late"
      ? `${t("table.unscored")}\n${status}`
      : status;
}

export function buildCourseworkReport(
  classroom: Classroom,
  students: Student[],
  assignments: HomeworkAssignment[],
  records: HomeworkRecord[],
  termLabel = "",
) {
  const t = i18n.getFixedT(null, "gradeOverview");
  const ordered = assignments
    .filter((item) => item.classId === classroom.id)
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.title.localeCompare(b.title));
  const rows = gradeOverview(classroom.id, students, ordered, records).students;
  const header = (item: HomeworkAssignment) =>
    `${item.title}\n${item.dueDate}${item.maxScore !== undefined ? ` · ${t("table.maximum", { score: item.maxScore })}` : ""}`;
  const sheet = (tasks: HomeworkAssignment[], title: string) => ({
    title,
    repeatColumns: [0, 1],
    headers: [t("table.number"), t("table.name"), ...tasks.map(header)],
    rows: rows.map((row) => [
      studentNumberLabel(row.student, true),
      row.student.name,
      ...tasks.map((item) =>
        courseworkCellText(item, row.entries.find((entry) => entry.assignment.id === item.id)?.record),
      ),
    ]),
  });
  const title = `${t("table.title")}${termLabel ? `｜${termLabel}` : ""}`;
  const report: RecordReport = {
    title,
    className: classroom.name,
    landscape: true,
    sheets: [sheet(ordered, t("table.title"))],
  };
  // Repeat student identity in each manageable horizontal print section.
  const sections: RecordReport["sheets"] = [];
  for (let offset = 0; offset < Math.max(1, ordered.length); offset += 6)
    sections.push(
      sheet(
        ordered.slice(offset, offset + 6),
        ordered.length > 6 ? `${t("table.title")} ${Math.floor(offset / 6) + 1}` : t("table.title"),
      ),
    );
  const printReport: RecordReport = { ...report, sheets: sections };
  return { report, printReport };
}
